import {
  type AdminInvoiceUsage,
  ApiError,
  adminInvoiceUsageGet,
} from '@cortex/web-api/clients/admin/api';
import { useEffect, useRef, useState } from 'react';

export function InvoiceUsage({ onSessionExpired }: { onSessionExpired: () => void }) {
  const [data, setData] = useState<AdminInvoiceUsage | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const controller = useRef<AbortController | null>(null);

  async function load() {
    controller.current?.abort();
    const request = new AbortController();
    controller.current = request;
    setLoading(true);
    setError(false);
    setData(null);
    try {
      const result = await adminInvoiceUsageGet({ signal: request.signal });
      if (!request.signal.aborted) setData(result);
    } catch (err) {
      if (request.signal.aborted) return;
      if (err instanceof ApiError && err.status === 401) onSessionExpired();
      else setError(true);
    } finally {
      if (!request.signal.aborted) setLoading(false);
    }
  }

  // biome-ignore lint/correctness/useExhaustiveDependencies: refresh is explicit; abort on unmount
  useEffect(() => {
    void load();
    return () => controller.current?.abort();
  }, []);

  return (
    <section
      className="card"
      aria-label="Invoice usage"
      aria-busy={loading}
      style={{ padding: 'var(--space-6)' }}
    >
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 16,
        }}
      >
        <h2 className="h3" style={{ color: 'var(--ink)' }}>
          Invoice usage · last 7 days
        </h2>
        <button type="button" className="btn-secondary" onClick={load} disabled={loading}>
          Refresh usage
        </button>
      </div>
      <p className="field-help" style={{ marginTop: 8, marginBottom: 16 }}>
        Invoices posted from AP1 and now in AP2, by client. Lowest activity first; each invoice
        counted once.
      </p>
      {loading && <output className="field-help">Checking invoice activity…</output>}
      {error && (
        <p className="field-error" role="alert">
          Invoice activity could not be loaded. Try refreshing.
        </p>
      )}
      {data && data.clients.length === 0 && <p className="field-help">No live clients yet.</p>}
      {data && data.clients.length > 0 && (
        <table
          aria-label="Invoices processed by client"
          style={{ width: '100%', borderCollapse: 'collapse', fontSize: 'var(--fs-small)' }}
        >
          <thead>
            <tr>
              <th
                scope="col"
                style={{
                  textAlign: 'left',
                  padding: '8px 0',
                  borderBottom: '1px solid var(--rule)',
                }}
              >
                Client
              </th>
              <th
                scope="col"
                style={{
                  textAlign: 'right',
                  padding: '8px 0',
                  borderBottom: '1px solid var(--rule)',
                }}
              >
                Invoices processed
              </th>
            </tr>
          </thead>
          <tbody>
            {data.clients.map((client) => (
              <tr key={client.slug}>
                <th
                  scope="row"
                  style={{
                    textAlign: 'left',
                    fontWeight: 400,
                    padding: '10px 0',
                    borderBottom: '1px solid var(--rule)',
                    overflowWrap: 'anywhere',
                  }}
                >
                  {client.displayName}
                </th>
                <td
                  style={{
                    textAlign: 'right',
                    padding: '10px 0',
                    borderBottom: '1px solid var(--rule)',
                    fontVariantNumeric: 'tabular-nums',
                    fontWeight: 600,
                  }}
                >
                  {client.status === 'ok'
                    ? client.count
                    : client.status === 'unsupported'
                      ? 'Not available on legacy AP'
                      : 'Unavailable'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {data && (
        <p className="field-help" style={{ marginTop: 12 }}>
          Rolling 7 days ending {new Date(data.to).toLocaleString()}. Unavailable is not zero
          activity.
        </p>
      )}
    </section>
  );
}
